/**
 * Simplified Data Structures for Analytics Batch Flush
 * Only essential structures used in actual analytics_batch_flush payload
 */

import { sendToLoggingService } from '../data/base-api.js'
import { MasterDataCollector } from './advancedDataCollectors.js'

export const BrowserHardwareMetrics = {
  browser: {
    name: null,
    version: null,
    user_agent: null,
    language: null
  },
  device: {
    type: null,
    os: null,
    local_time: {
      current_local_time: null,
      timezone: null,
      timezone_offset: null,
      locale: null
    },
    screen: {
      width: null,
      height: null
    }
  },
  hardware: {
    memory: {
      total_js_heap: null,
      used_js_heap: null,
      device_memory: null
    },
    cpu: {
      cores: null
    }
  },
  timestamp: new Date().toISOString()
}

export const PerformanceMetrics = {
  timing: {
    duration: null,
    start_time: null,
    end_time: null
  },
  resource_usage: {
    bandwidth_usage: null,
    cpu_usage: null,
    memory_peak: null
  }
}

export const ContentMetrics = {
  content_type: null,
  content_size: null,
  load_time: null,
  completion_rate: null
}

export const ErrorDiagnostics = {
  errors: [],
  warnings: []
}

export class IndexedDBManager {
  constructor() {
    this.dbName = 'StickearnAnalytics'
    this.dbVersion = 1
    this.db = null
    this.isReady = false
  }

  initialize() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)

      request.onerror = () => {
        console.error('IndexedDB failed to open:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        this.isReady = true
        console.log('🗃️ IndexedDB initialized successfully')
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result

        if (!db.objectStoreNames.contains('analytics_events')) {
          const store = db.createObjectStore('analytics_events', {
            keyPath: 'id',
            autoIncrement: true
          })
          store.createIndex('timestamp', 'timestamp', { unique: false })
          store.createIndex('event_type', 'event_type', { unique: false })
          store.createIndex('session_id', 'session_id', { unique: false })
        }

        if (!db.objectStoreNames.contains('session_summary')) {
          const store = db.createObjectStore('session_summary', {
            keyPath: 'session_id'
          })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }

        if (!db.objectStoreNames.contains('batch_queue')) {
          const store = db.createObjectStore('batch_queue', {
            keyPath: 'batch_id'
          })
          store.createIndex('created_at', 'created_at', { unique: false })
          store.createIndex('status', 'status', { unique: false })
        }

        console.log('🗃️ IndexedDB stores created')
      }
    })
  }

  async storeEvent(eventType, eventData, sessionId) {
    if (!this.isReady) {
      await this.initialize()
    }

    const transaction = this.db.transaction(['analytics_events'], 'readwrite')
    const store = transaction.objectStore('analytics_events')

    const event = {
      event_type: eventType,
      session_id: sessionId,
      timestamp: Date.now(),
      data: eventData,
      size_bytes: JSON.stringify(eventData).length
    }

    return new Promise((resolve, reject) => {
      const request = store.add(event)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getEventsBatch(limit = 50) {
    if (!this.isReady) {
      await this.initialize()
    }

    const transaction = this.db.transaction(['analytics_events'], 'readonly')
    const store = transaction.objectStore('analytics_events')

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => {
        const events = request.result.slice(0, limit)
        resolve(events)
      }
      request.onerror = () => reject(request.error)
    })
  }

  async clearProcessedEvents(eventIds) {
    if (!this.isReady) {
      await this.initialize()
    }

    const transaction = this.db.transaction(['analytics_events'], 'readwrite')
    const store = transaction.objectStore('analytics_events')

    const promises = eventIds.map((id) => {
      return new Promise((resolve, reject) => {
        const request = store.delete(id)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    })

    return Promise.all(promises)
  }

  async storeSessionSummary(sessionId, summary) {
    if (!this.isReady) {
      await this.initialize()
    }

    const transaction = this.db.transaction(['session_summary'], 'readwrite')
    const store = transaction.objectStore('session_summary')

    const sessionData = {
      session_id: sessionId,
      timestamp: Date.now(),
      summary: summary
    }

    return new Promise((resolve, reject) => {
      const request = store.put(sessionData)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getStorageInfo() {
    if (!this.isReady) {
      await this.initialize()
    }

    const transaction = this.db.transaction(['analytics_events'], 'readonly')
    const store = transaction.objectStore('analytics_events')

    return new Promise((resolve, reject) => {
      const request = store.count()
      request.onsuccess = () => {
        resolve({
          event_count: request.result,
          estimated_size_kb: request.result * 2,
          last_updated: Date.now()
        })
      }
      request.onerror = () => reject(request.error)
    })
  }
}

// ========================
// 📦 BATCH COLLECTION MANAGER
// ========================
export class BatchCollectionManager {
  constructor(options = {}) {
    this.options = {
      batchSize: options.batchSize || 25,
      flushInterval: options.flushInterval || 30000,
      debug: options.debug || false
    }
    this.dbManager = new IndexedDBManager()
    this.lastFlushTime = 0
    this.sessionId = options.sessionId || null
    this.screenUuid = new URLSearchParams(window.location.search).get('screenUuid') || 'default'
    this.sessionData = {
      batch_summary: this.createEmptySessionSummary()
    }
    this.advancedDataCollector = new MasterDataCollector()
    this.debugLog('📦 BatchCollectionManager initialized:', this.options)
  }

  debugLog(message, ...args) {
    if (this.options.debug) {
      console.log(message, ...args)
    }
  }

  async initialize(sessionId) {
    await this.dbManager.initialize()
    this.sessionId = sessionId
    this.sessionData.batch_summary.session_id = sessionId

    try {
      await this.advancedDataCollector.initialize()
      this.debugLog('📊 Advanced data collector initialized')
    } catch (error) {
      console.warn('Failed to initialize advanced data collector:', error)
    }

    this.debugLog(`📦 BatchCollectionManager initialized with session: ${sessionId}`)
    return Promise.resolve()
  }

  async addEvent(eventName, eventData) {
    this.debugLog(`📝 Event: ${eventName}`)
    this.sessionData.batch_summary.total_events++
    this.processEventForSummary(this.sessionData.batch_summary, eventName, eventData)
    this.updateKPIs(this.sessionData.batch_summary)

    await this.dbManager.storeEvent(eventName, eventData, this.sessionId)
    await this.dbManager.storeSessionSummary(this.sessionId, this.sessionData.batch_summary)
    return true
  }

  async flushBatch() {
    this.debugLog('🔍 flushBatch called')
    if (!this.alreadyFlushed) {
      this.alreadyFlushed = true
      console.log('📊 Sending analytics_batch_flush...')

      let advancedMetrics = null
      try {
        this.debugLog('📊 Collecting advanced metrics for analytics_batch_flush...')
        advancedMetrics =
          await this.advancedDataCollector.collectAllMetrics('analytics_batch_flush')
      } catch (error) {
        console.warn('Failed to collect advanced metrics:', error)
      }

      const events = await this.dbManager.getEventsBatch(this.options.batchSize)

      const summary = this.createEmptySessionSummary()
      for (const event of events) {
        this.processEventForSummary(summary, event.event_type, event.data)
      }
      this.updateKPIs(summary)
      summary.end_time = new Date().toISOString()
      summary.session_duration = Date.now() - new Date(summary.start_time).getTime()
      this.sessionData.batch_summary = summary

      const analyticsPayload = {
        timestamp: new Date().toISOString(),
        event_name: 'analytics_batch_flush',
        event_level: 'info',
        event_usage: 'analytics_batch',
        message: 'Session completed - batch flush triggered',
        repository: 'screen-app-html5',
        environment: this.getEnvironment(),
        event_source: 'nodejs',
        device_user_agent: navigator.userAgent,
        device_session_id: this.sessionId || 'unknown',
        device_screen_width: window.screen?.width || 0,
        device_screen_height: window.screen?.height || 0,
        device_os_name: navigator.platform,
        event_properties: {
          mode: window.location.pathname?.includes('basic') ? 'light' : 'normal',
          session_summary: this.sessionData.batch_summary,
          batch_metadata: {
            flush_timestamp: Date.now(),
            flush_trigger: 'content_completion',
            session_completed: true,
            event_count: events.length
          },
          advanced_metrics: {
            browser_hardware: advancedMetrics?.browser_hardware || {},
            performance: advancedMetrics?.performance || {},
            content: advancedMetrics?.content || {},
            diagnostics: advancedMetrics?.diagnostics || {},
            network_info: {
              connection_type: navigator.connection?.type || 'unknown',
              effective_type: navigator.connection?.effectiveType || 'unknown',
              downlink: navigator.connection?.downlink || null,
              rtt: navigator.connection?.rtt || null
            },
            context: {
              url: window.location.href,
              referrer: document.referrer,
              user_agent: navigator.userAgent,
              language: navigator.language,
              platform: navigator.platform,
              screen_resolution: `${window.screen.width}x${window.screen.height}`,
              viewport_size: `${window.innerWidth}x${window.innerHeight}`,
              color_depth: window.screen.colorDepth,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              local_time: new Date().toLocaleString()
            }
          },
          individual_events: events.map((e) => ({
            type: e.event_type,
            timestamp: e.timestamp,
            data_size: e.size_bytes
          }))
        }
      }

      this.debugLog('📦 Analytics payload prepared')
      try {
        this.debugLog('🌐 Calling sendToLoggingService...')
        await sendToLoggingService(analyticsPayload)
        console.log('✅ Analytics batch sent successfully')
        this.alreadyFlushed = false
        await this.dbManager.clearProcessedEvents(events.map((e) => e.id))
        return true
      } catch (error) {
        console.error('❌ Analytics batch error:', error)
        return false
      }
    } else {
      this.debugLog('❌ Flush conditions not met')
    }
    return false
  }

  getEnvironment() {
    return process.env.MODE || 'production'
  }

  createEmptySessionSummary() {
    return {
      session_id: this.sessionId,
      screen_uuid: this.screenUuid,
      start_time: new Date().toISOString(),
      end_time: null,
      session_duration: 0,
      total_events: 0,
      content_events: 0,
      user_interactions: 0,
      performance_metrics: {
        page_load_time: performance.now(),
        memory_usage: this.getMemoryInfo(),
        screen_size: `${window.screen.width}x${window.screen.height}`
      },
      totals: {
        ad_requests: 0,
        successful_requests: 0,
        failed_requests: 0,
        vast_started: 0,
        vast_first_quartile: 0,
        vast_midpoint: 0,
        vast_third_quartile: 0,
        vast_complete: 0,
        video_starts: 0,
        video_first_quartile: 0,
        video_midpoint: 0,
        video_third_quartile: 0,
        video_completions: 0,
        playlog_sends: 0,
        successful_tracks: 0,
        failed_tracks: 0,
        errors: 0,
        warnings: 0
      },
      kpis: {
        request_success_rate: 0,
        vast_completion_rate: 0,
        video_completion_rate: 0,
        tracking_success_rate: 0,
        error_rate: 0
      }
    }
  }

  getMemoryInfo() {
    if ('memory' in performance) {
      return {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      }
    }
    return null
  }

  updateKPIs(summary) {
    const totals = summary.totals
    if (totals.ad_requests > 0) {
      summary.kpis.request_success_rate = (
        (totals.successful_requests / totals.ad_requests) *
        100
      ).toFixed(2)
    }
    if (totals.vast_started > 0) {
      summary.kpis.vast_completion_rate = (
        (totals.vast_complete / totals.vast_started) *
        100
      ).toFixed(2)
    }
    if (totals.video_starts > 0) {
      summary.kpis.video_completion_rate = (
        (totals.video_completions / totals.video_starts) *
        100
      ).toFixed(2)
    }
    if (totals.playlog_sends > 0) {
      summary.kpis.tracking_success_rate = (
        (totals.successful_tracks / totals.playlog_sends) *
        100
      ).toFixed(2)
    }
    if (summary.total_events > 0) {
      summary.kpis.error_rate = ((totals.errors / summary.total_events) * 100).toFixed(2)
    }
  }

  processEventForSummary(summary, eventType, eventData) {
    const now = eventData?.timestamp || new Date().toISOString()

    const eventLog = {
      startTime: now,
      endTime: null,
      url: eventData?.url || eventData?.filename || null,
      type: eventData?.type || null,
      completionType: eventType,
      screen_uuid: summary.screen_uuid,
      id: eventData?.id
    }

    if (!summary.event_logs) {
      summary.event_logs = []
    }
    const lastLog = summary.event_logs
      .slice()
      .reverse()
      .find((log) => log.completionType === eventType && log.endTime === null)
    // --- Existing counter logic ---
    switch (eventType) {
      case 'ad_request_start':
        summary.totals.ad_requests++
        break
      case 'post_ad_request_success':
        summary.totals.successful_requests++
        break
      case 'post_ad_request_failed':
        summary.totals.failed_requests++
        break
      case 'vast_start':
      case 'vast_started':
      case 'vast_playback_started':
        summary.totals.vast_started++
        eventLog.completionType = 'vast_started'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'vast_first_quartile':
      case 'firstQuartile':
        summary.totals.vast_first_quartile++
        eventLog.completionType = 'vast_first_quartile'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'vast_midpoint':
      case 'midpoint':
        summary.totals.vast_midpoint++
        eventLog.completionType = 'vast_midpoint'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'vast_third_quartile':
      case 'thirdQuartile':
        summary.totals.vast_third_quartile++
        eventLog.completionType = 'vast_third_quartile'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'vast_complete':
      case 'vast_completed':
      case 'vast_playback_completed':
        summary.totals.vast_complete++
        eventLog.completionType = 'vast_complete'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'video_start':
      case 'video_playback_started':
        summary.totals.video_starts++
        eventLog.completionType = 'video_starts'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'video_first_quartile':
      case 'video_playback_first_quartile':
        summary.totals.video_first_quartile++
        eventLog.completionType = 'video_first_quartile'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break

      case 'video_midpoint':
      case 'video_playback_midpoint':
        summary.totals.video_midpoint++
        eventLog.completionType = 'video_midpoint'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'video_third_quartile':
      case 'video_playback_third_quartile':
        summary.totals.video_third_quartile++
        eventLog.completionType = 'video_third_quartile'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'video_complete':
      case 'video_playback_completed':
        summary.totals.video_completions++
        eventLog.completionType = 'video_completions'
        if (lastLog) {
          lastLog.endTime = now
        } else {
          eventLog.endTime = now
          summary.event_logs.push(eventLog)
        }
        break
      case 'sendPlaylog_success':
        summary.totals.playlog_sends++
        summary.totals.successful_tracks++
        break
      case 'sendPlaylog_failed':
        summary.totals.playlog_sends++
        summary.totals.failed_tracks++
        break
      case 'app_error':
      case 'vast_error':
      case 'video_error':
        summary.totals.errors++
        break
      case 'app_warning':
        summary.totals.warnings++
        break
      default:
        break
    }
    summary.total_events++
    if (
      [
        'vast_start',
        'vast_started',
        'vast_playback_started',
        'vast_first_quartile',
        'firstQuartile',
        'vast_midpoint',
        'midpoint',
        'vast_third_quartile',
        'thirdQuartile',
        'vast_complete',
        'vast_completed',
        'vast_playback_completed',
        'video_start',
        'video_playback_started',
        'video_complete',
        'video_playback_completed'
      ].includes(eventType)
    ) {
      summary.content_events++
    }
  }
}
